import { ChevronDown, MessageCircle } from 'lucide-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import { useIsBreakpoint } from '@/hooks/use-is-breakpoint';
import { supabase } from '@/lib/supabase';
import { StyledTextArea } from '@/Shared/StyledElements';
import { useCourseEditStore } from '@/stores/courseEditStore';

import CourseEditContext from '../CourseEditContext';

import CourseThreadItem from './CourseThreadItem';
import { groupCommentsWithReplies, replyToComment, sortComments } from './utils';

const AVATAR_PALETTES = [
	{ bg: '#dbeafe', text: '#1d4ed8' },
	{ bg: '#d1fae5', text: '#065f46' },
	{ bg: '#fef3c7', text: '#92400e' },
	{ bg: '#ffe4e6', text: '#9f1239' },
	{ bg: '#ede9fe', text: '#5b21b6' },
	{ bg: '#cffafe', text: '#155e75' },
];

const getAvatarStyle = (name: string) =>
	AVATAR_PALETTES[(name.charCodeAt(0) ?? 0) % AVATAR_PALETTES.length];

const CourseComments = () => {
	const { courseData, currentUserData, isApprovalMode } = useContext(CourseEditContext);
	const { commentTypes } = useCourseEditStore();
	const [comments, setComments] = useState<UserComment[]>([]);
	const isMobile = useIsBreakpoint('max', 768);
	const [isCollapsed, setIsCollapsed] = useState(() => isMobile);
	const [isAddingComment, setIsAddingComment] = useState(false);
	const [newCommentText, setNewCommentText] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const addCommentTextareaRef = useRef<HTMLTextAreaElement>(null);

	const commentFilter = isApprovalMode ? 'submission' : 'general';

	const fetchComments = useCallback(async () => {
		const { data, error } = await supabase
			.from('course_comments')
			.select('*')
			.eq('course_id', courseData.course_id)
			.eq('comment_type', commentFilter)
			.order('created_at', { ascending: true });

		if (error) {
			console.error('Error fetching comments:', error);
			return;
		}

		if (data) {
			const filteredComments = data.filter((x) => {
				const regex = new RegExp(commentTypes.join('|'));
				return regex.test(x.editor_type);
			});
			const groupedComments = groupCommentsWithReplies(filteredComments);
			setComments(sortComments(groupedComments));
		}
	}, [courseData.course_id, commentFilter, commentTypes]);

	useEffect(() => {
		fetchComments();

		const channel = supabase
			.channel(`course_comments_${courseData.course_id}`)
			.on(
				'postgres_changes',
				{
					event: '*',
					schema: 'public',
					table: 'course_comments',
					filter: `course_id=eq.${courseData.course_id}`,
				},
				() => fetchComments()
			)
			.subscribe();

		return () => {
			supabase.removeChannel(channel);
			setComments([]);
		};
	}, [courseData.course_id, fetchComments]);

	const {
		user_first: firstName = '',
		user_last: lastName = '',
		user_id: userId = '',
	} = currentUserData ?? {};

	const commentCount = comments.length;

	const handleAddComment = async () => {
		const text = newCommentText.trim();
		if (!text || !userId) return;

		setIsSubmitting(true);
		try {
			const { error } = await supabase.from('course_comments').insert({
				course_id: courseData.course_id,
				text,
				user_name: `${firstName} ${lastName}`,
				user_id: userId,
				editor_type: commentTypes[0] ?? 'general',
				comment_type: isApprovalMode ? 'submission' : 'general',
				school_id: import.meta.env.VITE_SCHOOL_ID,
			});
			if (error) throw error;
			showSuccessToast('Comment added');
			setNewCommentText('');
			setIsAddingComment(false);
		} catch {
			showErrorToast('Comment could not be added.');
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="flex flex-col h-full bg-background rounded-lg border overflow-hidden">
			{/* Collapsible header */}
			<div className="flex w-full items-center justify-between px-3 py-2.5 border-b border-border bg-background">
				<button
					type="button"
					onClick={() => setIsCollapsed((v) => !v)}
					className="flex flex-1 items-center gap-2 text-left min-w-0"
					aria-expanded={!isCollapsed}
				>
					<span className="text-xs text-foreground">Comments</span>
					{commentCount > 0 && (
						<span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold bg-primary/10 text-primary rounded-full leading-none">
							{commentCount}
						</span>
					)}
				</button>
				<div className="flex items-center gap-0.5 shrink-0">
					{!isAddingComment && (
						<Button
							type="button"
							variant="secondary"
							size="xs"
							aria-label="Add comment"
							onClick={() => {
								setIsCollapsed(false);
								setIsAddingComment(true);
								setTimeout(() => addCommentTextareaRef.current?.focus(), 0);
							}}
							className="p-1.5 hover:bg-secondary text-muted-foreground hover:text-primary transition-colors"
						>
							<span className="text-xs text-foreground">Add Comment</span>
						</Button>
					)}
					<button
						type="button"
						onClick={() => setIsCollapsed((v) => !v)}
						className="p-1.5 rounded hover:bg-secondary transition-colors"
						aria-label={isCollapsed ? 'Expand comments' : 'Collapse comments'}
					>
						<ChevronDown
							size={14}
							className={`text-muted-foreground transition-[rotate] duration-200 shrink-0 ${isCollapsed ? '-rotate-90' : ''}`}
						/>
					</button>
				</div>
			</div>
			{/* Inline add comment form */}
			{isAddingComment && !isCollapsed && (
				<div className="px-3 py-2.5 border-b border-border flex flex-col gap-2 bg-secondary/60">
					<StyledTextArea
						ref={addCommentTextareaRef}
						placeholder="Add a comment…"
						value={newCommentText}
						onChange={(e) => setNewCommentText(e.target.value)}
						rows={3}
					/>
					<div className="flex gap-2 justify-end">
						<Button
							type="button"
							variant="secondary"
							size="xs"
							onClick={() => {
								setIsAddingComment(false);
								setNewCommentText('');
							}}
						>
							Cancel
						</Button>
						<Button
							type="button"
							size="xs"
							disabled={!newCommentText.trim() || isSubmitting}
							onClick={handleAddComment}
						>
							Add Comment
						</Button>
					</div>
				</div>
			)}
			{/* Body */}
			{!isCollapsed && (
				<div className="flex-1 overflow-auto">
					{commentCount === 0 && (
						<div className="flex flex-col items-center justify-center py-10 px-4 text-center gap-2">
							<MessageCircle size={28} className="text-muted-foreground/30" />
							<p className="text-sm font-medium text-muted-foreground">No comments yet</p>
							<p className="text-xs text-muted-foreground/60 leading-snug">
								Select text in the editor and click the&nbsp;
								<MessageCircle size={10} className="inline" />
								&nbsp;icon to add one
							</p>
						</div>
					)}

					{commentCount > 0 && (
						<div className="p-2 flex flex-col gap-4">
							{comments.map((comment: UserComment) => {
								const av = getAvatarStyle(comment.userName ?? '');
								const initial = (comment.userName ?? '?').charAt(0).toUpperCase();

								return (
									<div
										key={comment.id}
										data-comment-source={`comment-${comment.id}`}
										className="bg-background overflow-hidden border-b border-border pb-2 last:border-b-0"
									>
										{/* Root comment */}
										<div className="p-3">
											<div className="flex items-start gap-2">
												<div
													className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5"
													style={{ backgroundColor: av.bg, color: av.text }}
												>
													{initial}
												</div>
												<div className="flex-1 min-w-0">
													<CourseThreadItem
														reply={comment}
														comment={comment}
														courseId={courseData.course_id}
														onCommentDeleted={fetchComments}
													/>
												</div>
											</div>
										</div>

										{/* Threaded replies */}
										{comment.thread?.length > 0 && (
											<div className="border-t border-border/30 px-3 pb-2 flex flex-col gap-1.5 pt-1.5">
												{comment.thread.map((reply: UserComment) => {
													const rav = getAvatarStyle(reply.userName ?? '');
													const ri = (reply.userName ?? '?').charAt(0).toUpperCase();

													return (
														<div
															key={reply.id}
															className="flex items-start gap-2 pl-2 border-l-2 border-primary/20"
														>
															<div
																className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5"
																style={{ backgroundColor: rav.bg, color: rav.text }}
															>
																{ri}
															</div>
															<div className="flex-1 min-w-0">
																<CourseThreadItem
																	reply={reply}
																	comment={comment}
																	courseId={courseData.course_id}
																	onCommentDeleted={fetchComments}
																/>
															</div>
														</div>
													);
												})}
											</div>
										)}

										{/* Reply form */}
										{currentUserData && (
											<div className="border-t border-border p-2.5">
												<form className="flex flex-col gap-1.5">
													<StyledTextArea placeholder="Reply…" name="comment" />
													<Button
														type="button"
														onClick={(e) =>
															replyToComment(
																e,
																comment,
																`${firstName} ${lastName}`,
																courseData.course_id,
																userId
															)
														}
														variant="secondary"
														size="xs"
														className="self-end"
													>
														Reply
													</Button>
												</form>
											</div>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default CourseComments;
